import React, { useMemo } from 'react'
import { X } from 'lucide-react'
import {
  revalidateStoredMarkerAnswer,
  isPartialValidationLevel
} from '../utils/exerciseMarkerValidation'
import { formatChordDetailed } from '../utils/chordFormatter'
import ChordLabel from './ChordLabel'
import { useBackdropDismiss } from '../hooks/useBackdropDismiss'
import './AttemptDetailModal.css'

/** Affiche un accord (réponse) avec ChordLabel pour emprunt (slash) et pédale (fraction). */
function ChordAnswerLabel({ answer }) {
  if (!answer) return '—'
  if (typeof answer === 'object' && answer.qcmAnswer != null) {
    const s = String(answer.qcmAnswer).trim()
    if (!s) return '—'
    if (['T', 'SD', 'D'].includes(s)) return s
    return <ChordLabel displayString={s} ariaLabel={s} />
  }
  if (
    typeof answer === 'object' &&
    (answer.selectedFunction != null || answer.function != null) &&
    !answer.degree &&
    !answer.specialRoot
  ) {
    return String(answer.selectedFunction ?? answer.function)
  }
  const detailed = formatChordDetailed(answer)
  if (detailed && detailed !== 'Non répondu') {
    return <ChordLabel chord={answer} displayString={detailed} ariaLabel={detailed} />
  }
  if (typeof answer === 'object' && answer.cadence) {
    return `${answer.degree || answer.selectedFunction || '—'} (${answer.cadence})`
  }
  return '—'
}

/**
 * Calcule les validations par question pour une tentative
 * (même chemin que saveAttempt, pour QCM / fonctions / saisie complète).
 */
export function getAttemptValidations(attempt) {
  const correctAnswers = attempt?.correctAnswers || []
  const userAnswers = attempt?.userAnswers || []
  const validations = []
  let correct = 0
  let partial = 0
  let incorrect = 0

  correctAnswers.forEach((correctAnswer, index) => {
    const userAnswer = userAnswers[index]
    const validation = revalidateStoredMarkerAnswer(userAnswer, correctAnswer, {
      index,
      progressionMode: attempt?.progressionMode,
      tonality: attempt?.tonality,
      mode: attempt?.mode || attempt?.dominantMode
    })
    validations.push({
      index: index + 1,
      userAnswer,
      correctAnswer,
      ...validation
    })
    if (validation.level === 1) correct++
    else if (isPartialValidationLevel(validation.level)) partial++
    else incorrect++
  })

  return {
    validations,
    summary: { correct, partial, incorrect, total: correctAnswers.length }
  }
}

function AttemptDetailModal({ attempt, exerciseTitle, onClose }) {
  const { validations, summary } = useMemo(
    () => (attempt ? getAttemptValidations(attempt) : { validations: [], summary: { correct: 0, partial: 0, incorrect: 0, total: 0 } }),
    [attempt]
  )
  const backdropDismiss = useBackdropDismiss(onClose)

  if (!attempt) return null

  const statusLabel = (level) => {
    if (level === 1) return { text: 'Correct', className: 'attempt-detail-status-correct' }
    if (isPartialValidationLevel(level)) return { text: 'Partiel', className: 'attempt-detail-status-partial' }
    return { text: 'Incorrect', className: 'attempt-detail-status-incorrect' }
  }

  return (
    <div className="attempt-detail-overlay" {...backdropDismiss} role="presentation">
      <div
        className="attempt-detail-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="attempt-detail-title"
      >
        <button
          type="button"
          className="attempt-detail-close"
          onClick={onClose}
          aria-label="Fermer"
        >
          <X size={20} />
        </button>
        <h3 id="attempt-detail-title" className="attempt-detail-title">
          Détail de la tentative
        </h3>
        <p className="attempt-detail-exercise">{exerciseTitle}</p>
        <div className="attempt-detail-summary">
          <span className="attempt-detail-score">Score : {attempt.score}%</span>
          <span className="attempt-detail-counts">
            <span className="attempt-detail-correct">✓ {summary.correct}</span>
            <span className="attempt-detail-partial">~ {summary.partial}</span>
            <span className="attempt-detail-incorrect">✗ {summary.incorrect}</span>
            <span className="attempt-detail-total">/ {summary.total}</span>
          </span>
        </div>
        <div className="attempt-detail-list">
          {validations.map((v) => {
            const status = statusLabel(v.level)
            const rowKind =
              v.level === 1 ? 'correct' : isPartialValidationLevel(v.level) ? 'partial' : 'incorrect'
            return (
              <div key={v.index} className={`attempt-detail-row attempt-detail-row-${rowKind}`}>
                <span className="attempt-detail-num">Q{v.index}</span>
                <span className="attempt-detail-user" title="Ta réponse">
                  <ChordAnswerLabel answer={v.userAnswer} />
                </span>
                <span className="attempt-detail-arrow">→</span>
                <span className="attempt-detail-correct-answer" title="Réponse attendue">
                  <ChordAnswerLabel answer={v.correctAnswer} />
                </span>
                <span className={`attempt-detail-status ${status.className}`}>{status.text}</span>
                <p className="attempt-detail-feedback">{v.feedback || ''}</p>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default AttemptDetailModal
